import {
  Component,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';

import {
  AwTableComponent,
  AwDividerComponent,
  AwButtonDirective,
  AwSpinnerComponent,
  AwDialogComponent,
  AwToastComponent,
  ToastVariants,
  DialogOptions,
  TableRow,
  TableCellInput,
  TableCellTypes,
  AwFooterComponent,
} from '@assetworks-llc/aw-component-lib';
import { AwDateFormComponent } from '../../../../shared/components/aw-date-form/aw-date-form.component';
import { TableActionBarComponent } from '../../../../shared/components/table-action-bar/table-action-bar.component';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { IntegrationService } from '../../../../core/services/integration.service';
import { AuthenticationService } from '../../../../core/services/authentication.service';
import { SecretService } from '../../../../core/services/secret.service';
import { EmployeeAuthenticationService } from '../../../../core/services/employee-authentication.service';
import { EmployeeService } from '../../../../core/services/employee.service';
import { UserService } from '../../../../core/services/user.service';
import { ParentIntegrationService } from '../../../../core/services/parent-integration.service';
import { Integration } from '../../../../api/models';
import { linkCellTemplate } from '../../../../shared/utils/link-cell-template';
import { injectIsPhone } from '../../../../shared/utils/breakpoints';
import { ResultsStatusComponent } from '../../../../shared/components/results-status/results-status.component';

@Component({
    selector: 'app-integration-management',
    imports: [
        AwSpinnerComponent,
        AwToastComponent,
        AwDialogComponent,
        AwTableComponent,
        AwDividerComponent,
        CommonModule,
        AwTableComponent,
        AwDividerComponent,
        AwDateFormComponent,
        AwButtonDirective,
        TableActionBarComponent,
        AwFooterComponent,
        ResultsStatusComponent,
    ],
    templateUrl: './integration-management.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './integration-management.component.scss',
        '../../../../../styles/management.scss',
    ]
})
export class IntegrationManagementComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly integrationService = inject(IntegrationService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly employeeAuthenticationService = inject(EmployeeAuthenticationService);
  private readonly secretService = inject(SecretService);
  private readonly employeeService = inject(EmployeeService);
  private readonly userService = inject(UserService);
  private readonly parentIntegrationService = inject(ParentIntegrationService);

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  /** Below 768px the table drops its height cap and the page scrolls (see .table-page in management.scss). */
  readonly isPhone = injectIsPhone();
  /** The table's current text filter, re-applied when the table is re-created across the phone breakpoint. */
  private tableFilterText = '';
  awTableComponent!: AwTableComponent;

  @ViewChild(AwTableComponent)
  set awTable(table: AwTableComponent | undefined) {
    if (!table) {
      return;
    }
    this.awTableComponent = table;
    if (this.tableFilterText) {
      table.applyFilter(this.tableFilterText);
    }
  }

  private applyTableFilter(text: string): void {
    this.tableFilterText = text;
    this.awTableComponent?.applyFilter(text);
  }

  isLoading: boolean = false;

  checkedRows: TableRow[] = [];

  tableData = signal<any[]>([]);
  tableDataCopy: any[] = [];
  selectedDate: Date | null = null;
  enableStickyOptions = signal(true);
  sortDisabled = signal(false);

  integrationToDelete: TableRow | null = null;

  openDeleteIntegrationDialog: boolean = false;

  /** Only AssetWorks employees add integrations (the add-integration route is gated the same way). */
  canAddIntegration: boolean = false;

  dialogOptionsDelete: DialogOptions = {
    variant: 'alert',
    title: `Delete Third-Party Integration`,
    description: `By selecting Delete, you will permanently delete this information. Choose Cancel, to go back to the view.`,
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Delete',
  };

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  private fetchIntegrationsFromIds(integrationIds: string[]): Observable<Integration[]> {
    if (!integrationIds.length) return of([]);

    return forkJoin(
      integrationIds.map(id =>
        this.integrationService.getIntegrationById({ organizationId: id })
          .pipe(catchError(() => of(null)))
      )
    ).pipe(
      map(integrations => integrations.filter((i): i is Integration => i !== null))
    );
  }

  ngOnInit(): void {
    this.isLoading = true;

    const organization = this.userService.getOrganization();
    const organizationId = this.userService.getUserInfo()?.organizationId || '';
    const userPermissions = this.userService.getUserPermissions();

    let integrations$: Observable<Integration[] | undefined>;

    if (organization === 'assetworks-employee') {
      integrations$ = this.integrationService.getAlIntegrations();

      this.canAddIntegration = true;

      if (userPermissions['canDelete']) {
        this.columnsDefinition.push({
          type: TableCellTypes.IconButton,
          key: 'random',
          label: 'Remove',
          buttonType: 'primary',
          isExpanded: false,
          icon: 'delete',
          action: (data: TableRow) => {
            this.openDeleteIntegrationDialog = true;
            this.integrationToDelete = data;
          },
        });
      }
    } else if (organization === 'integration-employee') {
      integrations$ = this.parentIntegrationService
        .getParentIntegrationById({ organizationId })
        .pipe(
          switchMap(parentIntegration =>
            this.fetchIntegrationsFromIds(parentIntegration?.integrationIds || [])
          ),
          catchError(() => of([]))
        );
    } else {
      integrations$ = of([]);
    }

    integrations$
      .pipe(
        finalize(() => (this.isLoading = false)),
        catchError((error) => {
          this.buildToastMessage('Failed to load third-party integrations', 'error');
          return of([]);
        })
      )
      .subscribe((integrations) => {
        const integrationData: any[] =
          integrations?.map((integration) => ({
            integrationInfo: {
              name: integration.organizationName,
              id: integration.organizationId,
              customers: integration.customerIds,
              link: 'https://www.google.com',
              detailsPageRoute: `/integration-management/integration-details/${integration.organizationId}`,
            },
            num_customers: integration.customerIds?.length ?? 0,
            modified_date: integration.modifiedOn
              ? new Date(integration.modifiedOn).toLocaleDateString()
              : 'N/A',
            date_created: integration.createdOn
              ? new Date(integration.createdOn).toLocaleDateString()
              : 'N/A',
          })) ?? [];
        this.tableData.set(integrationData);
        this.tableDataCopy = integrationData;
      });
  }

  columnsDefinition: TableCellInput[] = [
    {
      align: 'left',
      type: TableCellTypes.Custom,
      isExpanded: false,
      // CCL sorts a combined column by its key (table.mdx, 'custom columns that use combineFields'), so the key is the
      // displayed name; an object or missing field left sorting dead.
      key: 'integrationInfo.name',
      combineFields: [
        'integrationInfo.name',
        'integrationInfo.detailsPageRoute',
        'integrationInfo.id',
      ],
      label: 'Third-Party Integration Name',
      combineTemplate: (values: any[]) => ({
        template: linkCellTemplate(values[0], values[1], values[2]),
      }),
      sort: true,
    },
    {
      type: TableCellTypes.Title,
      key: 'num_customers',
      label: 'Number of Customers',
      isExpanded: false,
      sort: true,
      align: 'right',
    },
    {
      type: TableCellTypes.Title,
      key: 'modified_date',
      label: 'Last Modified',
      isExpanded: false,
      sort: true,
      align: 'center',
    },
    {
      type: TableCellTypes.Title,
      key: 'date_created',
      label: 'Created On',
      isExpanded: false,
      sort: true,
      align: 'right',
    },
  ];

  openAddIntegration(): void {
    this.router.navigate(['/integration-management/add-integration']);
  }

  onDateSelected(date: Date | null) {
    if (date === null) {
      this.applyTableFilter('');
      return;
    }
    const filteredDate: string = date!.toLocaleDateString();
    this.applyTableFilter(filteredDate);
  }
  dialogFormDeleteHandler(formValues: any) {
    if (this.integrationToDelete) {
      const integrationId: string =
        this.integrationToDelete['integrationInfo'].id;

      this.isLoading = true;

      this.secretService
        .listAllSecrets({ organizationId: integrationId })
        .pipe(
          mergeMap((secrets) => {
            if (!secrets || secrets.length === 0) {
              return of([]);
            }
            return forkJoin(
              secrets.map((secret) =>
                this.secretService
                  .deleteSecret({
                    organizationId: integrationId,
                    secretName: secret.name ?? '',
                  })
                  .pipe(catchError(() => of(false)))
              )
            );
          }),
          catchError(() => of([])),
          mergeMap(() =>
            this.employeeService
              .getEmployeesByOrganization({ organizationId: integrationId })
              .pipe(
                mergeMap((employees) => {
                  if (!employees || employees.length === 0) {
                    return of([]);
                  }
                  return forkJoin(
                    employees.map((employee) =>
                      this.employeeService
                        .deleteEmployee({ organizationId: integrationId, employeeId: employee.id! })
                        .pipe(catchError(() => of(false)))
                    )
                  );
                }),
                catchError(() => of([]))
              )
          ),
          mergeMap(() =>
            forkJoin([
              this.authenticationService
                .getAuthenticationConfig({ organizationId: integrationId })
                .pipe(
                  mergeMap(() =>
                    this.authenticationService.deleteAuthenticationConfig({ organizationId: integrationId })
                  ),
                  catchError(() => of(false))
                ),
              this.employeeAuthenticationService
                .getAuthenticationConfig({ organizationId: integrationId })
                .pipe(
                  mergeMap(() =>
                    this.employeeAuthenticationService.deleteAuthenticationConfig({ organizationId: integrationId })
                  ),
                  catchError(() => of(false))
                ),
            ])
          ),
          mergeMap(() =>
            this.integrationService.deleteIntegration({
              organizationId: integrationId,
            })
          ),
          finalize(() => {
            this.isLoading = false;
            this.openDeleteIntegrationDialog = false;
            this.integrationToDelete = null;
          })
        )
        .subscribe({
          next: (deleted) => {
            const filtered = this.tableDataCopy.filter(
              (row) => row['integrationInfo'].id !== integrationId
            );
            this.tableData.set(filtered);
            this.tableDataCopy = [...filtered];
            this.buildToastMessage(
              'Successfully deleted third-party integration',
              'success'
            );
          },
          error: () => {
            this.buildToastMessage('Failed to delete third-party integration', 'error');
          }
        });
    }
  }
  dialogCloseHandler() {
    this.openDeleteIntegrationDialog = false;
  }
}
